import { Power, PowerOff } from 'lucide-react'
import React, { useState } from 'react'
import { toast } from 'sonner'

import {
  usePostAdminPlanMappingsAction,
} from 'utils/web_api'

import { ConfirmationDialog } from '../../reusable/ConfirmationDialog'
import DisplayTooltipOnHover from '../../reusable/DisplayTooltipOnHover'

interface PlanMappingActionsProps {
  planMappingId: string
  planName: string
  isActive: boolean
  onSuccess?: () => void
}

export function PlanMappingActions ({
  planMappingId,
  planName,
  isActive,
  onSuccess,
}: PlanMappingActionsProps) {
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    action: 'deactivate' | 'reactivate'
  }>({
    open: false,
    action: 'deactivate',
  })

  // Mutation
  const toggleMutation = usePostAdminPlanMappingsAction()

  const handleDeactivateClick = () => {
    setConfirmDialog({
      open: true,
      action: 'deactivate',
    })
  }

  const handleReactivateClick = () => {
    setConfirmDialog({
      open: true,
      action: 'reactivate',
    })
  }

  const handleConfirmAction = async () => {
    try {
      await toggleMutation.mutateAsync({
        action: confirmDialog.action,
        data: {
          plan_mapping_id: planMappingId,
        },
      })
      toast.success('Plan mapping updated successfully')
      setConfirmDialog({ open: false, action: 'deactivate' })
      onSuccess?.()
    } catch (error: unknown) {
      const errorMessage = (error as any).message || `Failed to ${confirmDialog.action} plan mapping`

      toast.error(errorMessage)
      console.error('Error:', error)
    }
  }

  const isSubmitting = toggleMutation.isPending

  return (
    <>
      <div className='flex items-center gap-4'>
        {isActive
          ? (
            <DisplayTooltipOnHover text='Deactivate mapping'>
              <div
                className='cursor-pointer h-full'
                onClick={handleDeactivateClick}
                data-testid='deactivate-mapping-button'
              >
                <PowerOff
                  size={20}
                  className={`stroke-current text-red-600 ${
                    toggleMutation.isPending ? 'opacity-50' : ''
                  }`}
                />
              </div>
            </DisplayTooltipOnHover>
            )
          : (
            <DisplayTooltipOnHover text='Reactivate mapping'>
              <div
                className='cursor-pointer h-full'
                onClick={handleReactivateClick}
                data-testid='reactivate-mapping-button'
              >
                <Power
                  size={20}
                  className={`stroke-current text-green-600 ${
                    toggleMutation.isPending ? 'opacity-50' : ''
                  }`}
                />
              </div>
            </DisplayTooltipOnHover>
            )}
      </div>

      <ConfirmationDialog
        open={confirmDialog.open}
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, open }))}
        title={`${confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Reactivate'} Plan Mapping`}
        description={
          confirmDialog.action === 'deactivate'
            ? `Are you sure you want to deactivate the mapping for "${planName}"? This will prevent members from renewing to the mapped plan.`
            : `Are you sure you want to reactivate the mapping for "${planName}"? This will allow members to renew to the mapped plan.`
        }
        submitLabel={confirmDialog.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
        onSubmit={handleConfirmAction}
        isSubmitting={isSubmitting}
        isDanger={confirmDialog.action === 'deactivate'}
      />
    </>
  )
}
